<script setup lang="ts">
import {
	useCurrentUser,
	useDeleteCurrentUserAvatar,
	useUpdateCurrentUser,
	useUpdateCurrentUserAvatar,
	useUpdateCurrentUserEmail,
} from "@/api/current-user";
import AppAvatar from "@/components/common/AppAvatar.vue";
import SettingsFooter from "@/components/common/SettingsFooter.vue";
import FileSelectHelper from "@/components/helpers/FileSelectHelper.vue";
import LanguageSelect from "@/components/helpers/LanguageSelect.vue";
import ThemeSelect from "@/components/helpers/ThemeSelect.vue";
import Preferences from "@/components/profile/Preferences.vue";
import UserProfilePasswordUpdate from "@/components/profile/UserProfilePasswordUpdate.vue";
import { useRoleLabel } from "@/composable/role-label";
import { wheneverOnce } from "@/composable/whenever-once";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { useQueryManager } from "@/stores/query-manager";
import { ThemeType, useThemeStore } from "@/stores/theme";
import { useConfirmLeave } from "@/use/confirm-leave";
import { isEqualForKeys } from "@/utils/compare";
import { mdiAccountCircle, mdiAccountCircleOutline, mdiEmailOutline } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { CurrentUser, UpdateUser } from "@soft-db/monitoring-api-client";
import { Badge, Divider, FormField, Icon, InputGroup, TextInput, useScreenSize } from "@soft-db/ui";
import * as _ from "lodash-es";
import { storeToRefs } from "pinia";

const { mdOrLower } = useScreenSize();
const isSheanOrThom = computed(() => user.value?.id == "19" || user.value?.id === "81");

const { t } = useI18n();
const snackbar = useSnackbarStore();

const updateUserAvatar = useUpdateCurrentUserAvatar();
const deleteUserAvatar = useDeleteCurrentUserAvatar();
const updateCurrentUser = useUpdateCurrentUser();
const updateUserEmail = useUpdateCurrentUserEmail();
const { theme } = storeToRefs(useThemeStore());
const queryManager = useQueryManager();

const { data: user } = useCurrentUser();

const roleLabel = useRoleLabel();

type ProfileUserForm = UpdateUser & { email?: string };
const { useField, setValues, values: formValues, handleSubmit } = useForm<ProfileUserForm>();
// const emailForm = useForm<{ email: string }>();

async function updateAvatar(file: File) {
	try {
		await updateUserAvatar.mutateAsync(file);
		snackbar.openOnSaved();
	} catch {
		snackbar.openOnError(t("snackbar.internalServerError"));
	}
}
async function deleteAvatar() {
	try {
		await deleteUserAvatar.mutateAsync();
		snackbar.openOnSaved();
	} catch {
		snackbar.openOnError(t("snackbar.internalServerError"));
	}
}

const isEmailAuth = computed(() => user.value?.auth.type === "email");

const name = useField("name", { isRequired: true });
const lang = useField("preferredLang", { isRequired: true });
const email = useField("email", { isRequired: isEmailAuth.value });

const themeField = ref<ThemeType>(theme.value);

function syncUser(user: CurrentUser) {
	setValues({
		name: user.name,
		preferredLang: user.preferredLang,
	});

	if (user.auth.type === "email") {
		setValues({
			email: user.auth.email,
		});
	}

	theme.value = themeField.value;
}
wheneverOnce(user, (user) => syncUser(user), { immediate: true });

const hasUserChanged = computed(() => {
	if (!user.value) {
		return false;
	}
	return !isEqualForKeys(formValues.value, user.value, ["name", "preferredLang"]);
});

const hasLangChanged = computed(() => {
	if (!user.value) return false;
	return user.value.preferredLang !== lang.data.value;
});

const hasEmailChanged = computed(() => {
	if (!user.value) return false;

	return user.value.auth.type === "email" && user.value.auth.email !== email.data.value;
});

const hasThemeChanged = computed(() => themeField.value !== theme.value);

const hasChanges = computed(() => {
	if (!user.value) return false;

	return hasUserChanged.value || hasEmailChanged.value || hasThemeChanged.value;
});

function undoChanges() {
	if (user.value) syncUser(user.value);
}

const save = handleSubmit(async (data) => {
	if (user.value == null) {
		return;
	}

	try {
		await queryManager.chainInvalidation(async () => {
			let updatedUser = await updateCurrentUser.mutateAsync({
				name: data.name,
				preferredLang: data.preferredLang,
			});

			if (hasEmailChanged.value && data.email != null) {
				updatedUser = await updateUserEmail.mutateAsync(data.email);
			}

			syncUser(updatedUser);
		});

		snackbar.openOnSaved();
	} catch {
		snackbar.openOnError();
	}
});

useConfirmLeave(() => hasChanges.value, t("common.unsavedChanges"));
</script>

<template>
	<div class="max-w-300 mx-auto p-4">
		<ConfigSection v-if="user" :title="t('common.profile')" :description="t('profile.info')">
			<div class="flex gap-4">
				<FileSelectHelper
					class="avatar flex size-48 items-center justify-center rounded-full"
					:showDelete="!!user.avatarUrl"
					@update="updateAvatar"
					@delete="deleteAvatar"
				>
					<AppAvatar class="rounded-full" :src="user.avatarUrl" :icon="mdiAccountCircle" />
				</FileSelectHelper>
				<div class="flex flex-col justify-evenly gap-2">
					<div v-if="user.auth.type === 'username'">
						<h5>{{ t("common.username") }}</h5>
						<Badge>{{ user.auth.username }}</Badge>
					</div>
					<div>
						<h5>{{ t("common.role") }}</h5>
						<Badge>{{ roleLabel(user.role) }}</Badge>
					</div>
				</div>
			</div>
			<FormField class="w-full" :label="t('profile.fullName')" :errors="name.errors.value">
				<InputGroup class="w-full">
					<Icon :d="mdiAccountCircleOutline" />
					<TextInput v-model="name.data.value" />
				</InputGroup>
			</FormField>
			<FormField
				v-if="user.auth.type === 'email'"
				class="w-full"
				:label="t('common.email')"
				:errors="email.errors.value"
			>
				<InputGroup class="w-full">
					<Icon :d="mdiEmailOutline" />
					<TextInput v-model="email.data.value" />
				</InputGroup>
			</FormField>
		</ConfigSection>
		<Divider />

		<UserProfilePasswordUpdate />
		<Divider />

		<ConfigSection
			:title="t('profile.localization')"
			:description="t('profile.localizationCaption')"
		>
			<div>
				<LanguageSelect v-model="lang.data.value" :label="$t('profile.language')" />
				<div v-if="hasLangChanged" class="px-2">
					<span v-if="lang.data.value === 'fr-CA'" class="text-secondary text-xs">
						La langue de préférence sera modifiée une fois les changements sauvegardés.
					</span>
					<span v-else class="text-secondary text-xs">
						Preferred language will be updated when saved.
					</span>
				</div>
			</div>
		</ConfigSection>
		<Divider />
		<ConfigSection :title="$t('profile.theme')" :description="$t('profile.themeCaption')">
			<ThemeSelect v-model:theme="themeField" />
		</ConfigSection>

		<template v-if="isSheanOrThom && !mdOrLower">
			<Divider />
			<Preferences />
		</template>
		<SettingsFooter class="mt-4" :hasChanges="hasChanges" @undo="undoChanges" @save="save" />
	</div>
</template>
